import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import requisitionService from "../service-api/requisitionService";
import requisitionSkillSetsService from "../service-api/requisitionSkillSetsService";
import candidateService from "../service-api/candidateService";

// Initial state
const initialState = {
  data: [],
  error: null,
  isLoading: true,
  isUpdatingScore: false,
  candidateID: "",
  singleRequisition: {},
  isSingleCandidateLoading: true,
  requisitionSkills: [],
  AllRequisitionSkillSets: [],
  preScreeningQuestions: [],
  linkedCandidates: [],
  linkedCandidate: [],
  requisitionDetails: [],
  candidatePrescreeningData: [],
  candidateSpecificQuestions: [],
};

export const fetchRequisition = createAsyncThunk(
  "data/fetchRequisition",
  async () => {
    const response = await requisitionService.fetchRequisitions();
    return response.data;
  },
);

export const fetchRequisitionInfoByID = createAsyncThunk(
  "data/fetchRequisitionInfoByID",
  async (id) => {
    const response = await requisitionService.fetchRequisitionInfoByID(id);
    return response.data;
  },
);

export const addRequisition = createAsyncThunk(
  "data/addRequisition",
  async ({ apiData, skillsList, questionsList }, { dispatch }) => {
    try {
      const response = await requisitionService.addRequisition(apiData);
      const requisitionID = response.data?.itl_requisitionid;
      await dispatch(
        updateMatchingCandidatesScore(requisitionID),
      );
      if (requisitionID) {
        await skillsList.map((item) => {
          const updatedSkillSetObj = {
            itl_experiencerange: item.itl_experiencerange,
            "itl_RequisitionID@odata.bind": `/itl_requisitions(${requisitionID})`,
            "itl_SkillSet@odata.bind": `/itl_skillsetmasters(${item.itl_skillset})`,
          };

          dispatch(addRequisitionSkillSet(updatedSkillSetObj));
        });

        if (questionsList.length > 0) {
          const questionsWithOdataBind = questionsList.map((question, index) => ({
            "itl_requisition@odata.bind": `/itl_requisitions(${requisitionID})`,
            itl_question: question.itl_question,
            itl_isactive: true,
            itl_questionorder: (index + 1).toString()
          }));

          await Promise.all(
            questionsWithOdataBind.map((item) =>
              dispatch(addPreScreeningQuestion({ obj: item, requisitionId: requisitionID })).unwrap(),
            ),
          );
        }
      }



      dispatch(fetchAllRequisitionSkillSets());
      dispatch(fetchRequisition());
      return response.data;
    } catch (error) {
      console.error("Error adding requisition:", error);
      throw error;
    }
  },
);

export const updateRequisition = createAsyncThunk(
  "data/updateRequisition",
  async ({ id, apiData, skillsList }, { dispatch }) => {
    const response = await requisitionService.updateRequisition(id, apiData);
    // Use a for...of loop for asynchronous skill updates
    for (const item of skillsList) {
      const skillID = item.itl_skillsetid;
      const skillsetObj = {
        "itl_SkillSet@odata.bind": `/itl_skillsetmasters(${item.itl_skillset})`,
        itl_experience: item.itl_experiencerange,
      };
      await dispatch(updateRequisitionSkillSet({ skillID, skillsetObj }));
    }
    await dispatch(fetchRequisition());
    await dispatch(fetchAllRequisitionSkillSets());
    return response.data;
  },
);

export const updateMatchingCandidatesScore = createAsyncThunk(
  "data/updateMatchingCandidatesScore",
  async (id) => {
    const response = await requisitionService.generateMatchingCandidatesOnRequisitionChange(id);
    return response.data;
  },
);

export const fetchAllRequisitionSkillSets = createAsyncThunk(
  "data/allRequisitionSkillSets",
  async () => {
    const response =
      await requisitionSkillSetsService.fetchAllRequisitionSkillSets();
    return response.data;
  },
);

export const fetchRequisitionSkillSet = createAsyncThunk(
  "data/requisitionSkillSets",
  async (id) => {
    const response =
      await requisitionSkillSetsService.fetchRequisitionSkillSet(id);
    return response.data;
  },
);

export const addRequisitionSkillSet = createAsyncThunk(
  "data/addRequisitionSkillSet",
  async (obj, { dispatch }) => {
    const response =
      await requisitionSkillSetsService.requisitionSkillSetsService(obj);
    dispatch(fetchRequisition());
    await dispatch(fetchAllRequisitionSkillSets());
    return response.data;
  },
);

export const updateRequisitionSkillSet = createAsyncThunk(
  "data/updatecandidateskilset",
  async ({ skillID, skillsetObj }, { dispatch }) => {
    const response =
      await requisitionSkillSetsService.updateRequisitionSkillSet(
        skillID,
        skillsetObj,
      );

    dispatch(fetchRequisition());
    return response.data;
  },
);

export const deleteRequisitionSkillSet = createAsyncThunk(
  "data/deleteRequisitionSkillset",
  async (id, { dispatch }) => {
    const response =
      await requisitionSkillSetsService.deleteRequisitionSkillSet(id);
    await dispatch(fetchAllRequisitionSkillSets());
    return response.data;
  },
);

//single requisition
export const fetchRequisitionById = createAsyncThunk(
  "data/fetchrequisitionbyid",
  async (id) => {
    const response = await requisitionService.fetchSingleRequisition(id);
    return response.data;
  },
);

export const fetchLinkedCandidatesByReqID = createAsyncThunk(
  "data/fetchLinkedCandidatesByReqID",
  async (id) => {
    const response = await requisitionService.fetchLinkedCandidatesByReqID(id);
    return response.data;
  },
);

export const fetchReqInLinkedCandidateByCandidateID = createAsyncThunk(
  "data/fetchReqInLinkedCandidateByCandidateID",
  async (id) => {
    const response =
      await requisitionService.fetchReqInLinkedCandidateByCandidateID(id);
    return response.data;
  },
);

export const fetchAllLinkedCandidatesByReqID = createAsyncThunk(
  "data/fetchAllLinkedCandidatesByReqID",
  async (id) => {
    const response =
      await requisitionService.fetchAllLinkedCandidatesByReqID(id);
    return response.data;
  },
);

export const addLinkedCandidate = createAsyncThunk(
  "data/addlinkedCandidate",
  async (obj, { dispatch }) => {
    const response = await requisitionService.addLinkedCandidate({
      "itl_CandidateID@odata.bind": `/contacts(${obj.id})`,
      "itl_RequisitionID@odata.bind": `/itl_requisitions(${obj.requisitionId})`,
    });
    await dispatch(generateLinkedCandidateScore({ candidateId: obj.id, requisitionId: obj.requisitionId }));
    return response.data;
  },
);

export const generateLinkedCandidateScore = createAsyncThunk(
  "data/generateLinkedCandidateScore",
  async ({ candidateId, requisitionId }, { dispatch }) => {
    const response = await requisitionService.generateLinkedCandidateScore(candidateId, requisitionId);
    // await dispatch(fetchRequisition());
    return response.data;
  },
);


export const deleteLinkedCandidate = createAsyncThunk(
  "data/deleteLinkedCandidate",
  async (id) => {
    const response = await requisitionService.deleteLinkedCandidate(id);
    return response.data;
  },
);

export const updateLinkedCandidate = createAsyncThunk(
  "data/updateLinkedCandidate",
  async ({ id, obj }) => {
    const response = await requisitionService.updateLinkedCandidate(id, obj);
    return response.data;
  },
);

export const generateMatchingCandidates = createAsyncThunk(
  "data/generateMatchingCandidates",
  async (id) => {
    const response = await candidateService.generateMatchingCandidates({
      id: id,
    });
    return response.data;
  },
);

export const addPreScreeningQuestion = createAsyncThunk(
  "data/addPreScreeningQuestion",
  async ({ obj, requisitionId }, { dispatch }) => {
    const response = await requisitionService.addPreScreeningQuestions(obj);
    await dispatch(fetchPreScreeningQuestions(requisitionId));
    return response.data;
  },
);

export const updatePreScreeningQuestion = createAsyncThunk(
  "data/updatePreScreeningQuestion",
  async ({ id, obj, requisitionId }, { dispatch }) => {
    const response = await requisitionService.updatePreScreeningQuestions(id, obj);
    await dispatch(fetchPreScreeningQuestions(requisitionId));
    return response.data;
  },
);

export const deletePreScreeningQuestion = createAsyncThunk(
  "data/deletePreScreeningQuestion",
  async ({ id, requisitionId }, { dispatch }) => {
    const response = await requisitionService.deletePreScreeningQuestions(id);
    if (requisitionId) {
      await dispatch(fetchPreScreeningQuestions(requisitionId));
    }
    return response.data;
  },
);

export const fetchPreScreeningQuestions = createAsyncThunk(
  "data/fetchPreScreeningQuestions",
  async (id) => {
    const response = await requisitionService.fetchPreScreeningQuestions(id);
    return response.data;
  },
);

export const fetchCandidateSpecificQuestions = createAsyncThunk(
  "data/fetchCandidateSpecificQuestions",
  async ({ candidateId, requisitionId }) => {
    const response = await requisitionService.fetchCandidateSpecificQuestions(candidateId, requisitionId);
    return response.data;
  },
);

export const getCandidatePrescreeningDataByReqID = createAsyncThunk(
  "data/getCandidatePrescreeningDataByReqID",
  async (id) => {
    const response = await requisitionService.getCandidatePrescreeningDataByReqID(id);
    return response.data.value;
  },
);

// Create slice
const requisitionSlice = createSlice({
  name: "requisition",
  initialState,
  reducers: {
    setRequisitionData: (state, action) => {
      state.isLoading = false;
      state.requisitionDetails = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Get All Requisition
    builder
      .addCase(fetchRequisition.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchRequisition.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload.value;
      })
      .addCase(fetchRequisition.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      });

    // Add Requisition
    builder
      .addCase(addRequisition.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(addRequisition.fulfilled, (state, action) => {
        state.isLoading = false;
        state.requisitionID = action.payload.itl_requisitionid;
        state.data = action.payload.value;
        // toast.success("New Requisition Added");
      })
      .addCase(addRequisition.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      });

    // Update Requisition
    builder
      .addCase(updateRequisition.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateRequisition.fulfilled, (state, action) => {
        state.isLoading = false;
        // state.data = action.payload.value;

      })
      .addCase(updateRequisition.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      });

    // fetch Requisition Info By ID
    builder
      .addCase(fetchRequisitionInfoByID.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchRequisitionInfoByID.fulfilled, (state, action) => {
        state.isLoading = false;
        state.requisitionDetails = action.payload;
      })
      .addCase(fetchRequisitionInfoByID.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      });

    // Get All Requisition Skill Sets
    builder
      .addCase(fetchAllRequisitionSkillSets.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchAllRequisitionSkillSets.fulfilled, (state, action) => {
        state.isLoading = false;
        state.AllRequisitionSkillSets = action.payload.value;
      })
      .addCase(fetchAllRequisitionSkillSets.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      });

    // Get All Requisition Skill Sets
    builder
      .addCase(fetchRequisitionSkillSet.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchRequisitionSkillSet.fulfilled, (state, action) => {
        state.isLoading = false;
        state.requisitionSkills = action.payload.value;
      })
      .addCase(fetchRequisitionSkillSet.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      });

    // Get Requisition by ID
    builder
      .addCase(fetchRequisitionById.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchRequisitionById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.singleRequisition = action.payload;
      })
      .addCase(fetchRequisitionById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      });

    // Get Linked Candidates by Req ID
    builder
      .addCase(fetchLinkedCandidatesByReqID.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchLinkedCandidatesByReqID.fulfilled, (state, action) => {
        state.isLoading = false;
        state.linkedCandidate = action.payload.value;
      })
      .addCase(fetchLinkedCandidatesByReqID.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      });

    // Get Requisition in Linked Candidates by Candidate ID
    builder
      .addCase(fetchReqInLinkedCandidateByCandidateID.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(
        fetchReqInLinkedCandidateByCandidateID.fulfilled,
        (state, action) => {
          state.isLoading = false;
          const index = action.payload.value.length;
          state.requisitionDetails = action.payload.value[index - 1];
        },
      )
      .addCase(
        fetchReqInLinkedCandidateByCandidateID.rejected,
        (state, action) => {
          state.isLoading = false;
          state.error = action.error.message;
        },
      );

    // Get All Candidates
    builder
      .addCase(fetchAllLinkedCandidatesByReqID.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchAllLinkedCandidatesByReqID.fulfilled, (state, action) => {
        state.isLoading = false;
        state.linkedCandidates = action.payload.value;
      })
      .addCase(fetchAllLinkedCandidatesByReqID.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      });

    // Add Linked Candidate
    builder
      .addCase(addLinkedCandidate.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(addLinkedCandidate.fulfilled, (state, action) => {
        state.isLoading = false;
      })
      .addCase(addLinkedCandidate.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      });

    // Delete Linked Candidate
    builder
      .addCase(deleteLinkedCandidate.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteLinkedCandidate.fulfilled, (state, action) => {
        state.isLoading = false;
      })
      .addCase(deleteLinkedCandidate.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      });

    // Update Linked Candidate
    builder
      .addCase(updateLinkedCandidate.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateLinkedCandidate.fulfilled, (state, action) => {
        state.isLoading = false;
      })
      .addCase(updateLinkedCandidate.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      });

    builder
      .addCase(updateMatchingCandidatesScore.pending, (state) => {
        state.isUpdatingScore = true;
      })
      .addCase(updateMatchingCandidatesScore.fulfilled, (state, action) => {
        state.isUpdatingScore = false;
      })
      .addCase(updateMatchingCandidatesScore.rejected, (state, action) => {
        state.isUpdatingScore = false;
        state.error = action.error.message;
      });


    // Delete Linked Candidate
    builder.addCase(generateMatchingCandidates.rejected, (state, action) => {
      state.error = action.error.message;
    });

    // Fetch Pre Screening Questions
    builder
      .addCase(fetchPreScreeningQuestions.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchPreScreeningQuestions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.preScreeningQuestions = action.payload.value;
      })
      .addCase(fetchPreScreeningQuestions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      });

    // Fetch Candidate Specific Pre Screening Questions
    builder
      .addCase(fetchCandidateSpecificQuestions.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchCandidateSpecificQuestions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.candidateSpecificQuestions = action.payload.value;
      })
      .addCase(fetchCandidateSpecificQuestions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      });

    // Get Candidate Pre Screening Data By Req ID
    builder
      .addCase(getCandidatePrescreeningDataByReqID.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getCandidatePrescreeningDataByReqID.fulfilled, (state, action) => {
        state.isLoading = false;
        state.candidatePrescreeningData = action.payload;
      })
      .addCase(getCandidatePrescreeningDataByReqID.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      });
  },
});

export const { setRequisitionData } = requisitionSlice.actions;
export default requisitionSlice.reducer;
